import { useCallback, useEffect, useMemo, useState } from 'react';
import { Api, type PanelRoleDto, type PanelUserDto } from '../application/panelApi';
import { ICON } from '../components/Icons';
import { LaneIcon } from '../components/LaneIcon';
import { confirmDialog, modal } from '../components/ModalHost';
import { toast } from '../components/ToastHost';
import { errText, fmtDate } from '../lib/format';
import { useApp } from '../store/AppContext';

type Draft = {
  displayName: string;
  username: string;
  role: string;
  passwordMode: 'generate' | 'manual';
  password: string;
};

const EMPTY_DRAFT: Draft = {
  displayName: '',
  username: '',
  role: 'operator',
  passwordMode: 'generate',
  password: '',
};

const ROLE_SECTIONS = [
  ['search', 'Поисковик'], ['neural', 'AI поиск'], ['proxy', 'Прокси'],
  ['launch', 'Запуск'], ['servers', 'Сервера'], ['users', 'Пользователи'],
  ['logs', 'Логи'], ['profiles', 'Профили'], ['settings', 'Общие настройки'],
] as const;
const ROLE_ACTIONS = [['view', 'Просмотр'], ['create', 'Создание'], ['edit', 'Изменение'], ['delete', 'Удаление']] as const;

const AVATAR_COLORS = [
  '#2563EB',
  '#7C3AED',
  '#0D9488',
  '#DB2777',
  '#EA580C',
  '#4F46E5',
  '#059669',
  '#B45309',
];

function avatarColor(seed: string): string {
  let h = 0;
  for (const ch of seed) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length]!;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '?';
  const second = parts.length > 1 ? parts[1]![0] : (parts[0]?.[1] ?? '');
  return (first + second).toUpperCase();
}

async function copyText(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast('Пароль скопирован в буфер обмена.', 'ok');
  } catch {
    toast('Не удалось скопировать автоматически. Выделите пароль и скопируйте его вручную.', 'wa');
  }
}

function PasswordReveal({ password }: { password: string }) {
  return (
    <div className="note" style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <code className="mono" style={{ flex: 1, wordBreak: 'break-all' }}>
          {password}
        </code>
        <button type="button" className="btn btn-sm" onClick={() => void copyText(password)}>
          Копировать
        </button>
      </div>
    </div>
  );
}

export function UsersPage() {
  const { isSuperadmin, permissions, hasPermission, userId, goto, audit } = useApp();
  const [users, setUsers] = useState<PanelUserDto[]>([]);
  const [roles, setRoles] = useState<PanelRoleDto[]>([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');
  const [sort, setSort] = useState<'newest' | 'name'>('newest');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, roleData] = await Promise.all([Api.users.list(), Api.roles.list()]);
      setUsers(data.users || []);
      setRoles(roleData.roles || []);
    } catch (error) {
      toast(errText(error), 'err');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasPermission('users.view')) {
      toast(
        'Раздел «Пользователи» доступен только суперадмину. Если нужен доступ, обратитесь к нему.',
        'wa',
      );
      goto('search');
      return;
    }
    void load();
  }, [goto, hasPermission, load]);

  const roleLabel = useCallback((id: string) => roles.find((role) => role.id === id)?.name || id, [roles]);
  const assignableRoles = roles.filter((role) => isSuperadmin || (role.id !== 'superadmin' && role.permissions.every((permission) => permissions.includes(permission))));

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.username.toLowerCase().includes(q) ||
        u.displayName.toLowerCase().includes(q) ||
        roleLabel(u.role).toLowerCase().includes(q),
    );
  }, [filter, users, roleLabel]);

  const openRole = (role?: PanelRoleDto) => {
    let draft = { name: role?.name || '', permissions: [...(role?.permissions || [])] };
    const close = modal(
      role ? 'Права роли' : 'Создать роль',
      <RoleForm initial={draft} readOnly={Boolean(role?.builtin)} onChange={(next) => { draft = next; }} />,
      role?.builtin ? <button type="button" className="btn btn-primary" data-close>Готово</button> : <>
        <button type="button" className="btn" data-close>Отмена</button>
        <button type="button" className="btn btn-primary" onClick={() => {
          void (async () => {
            try {
              if (role) await Api.roles.update(role.id, draft);
              else await Api.roles.create(draft);
              close();
              await load();
              audit(role ? 'Изменил роль' : 'Создал роль', draft.name);
              toast(role ? 'Роль сохранена.' : 'Роль создана.', 'ok');
            } catch (error) { toast(errText(error), 'err'); }
          })();
        }}>{role ? 'Сохранить' : 'Создать роль'}</button>
      </>,
      true,
    );
  };

  const removeRole = (role: PanelRoleDto) => {
    confirmDialog('Удалить роль?', `Роль «${role.name}» будет удалена.`, () => {
      void (async () => {
        try {
          await Api.roles.remove(role.id);
          setRoleFilter('all');
          await load();
          audit('Удалил роль', role.name);
          toast('Роль удалена.', 'ok');
        } catch (error) { toast(errText(error), 'err'); }
      })();
    }, { yes: 'Удалить' });
  };

  const openCreate = () => {
    let draft: Draft = { ...EMPTY_DRAFT, role: assignableRoles[0]?.id || 'operator' };
    const close = modal(
      'Новый пользователь',
      <UserForm
        mode="create"
        initial={draft}
        roles={assignableRoles}
        onChange={(next) => {
          draft = next;
        }}
      />,
      <>
        <button type="button" className="btn" data-close>
          Отмена
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            void (async () => {
              try {
                const res = await Api.users.create({
                  username: draft.username,
                  displayName: draft.displayName,
                  role: draft.role,
                  generatePassword: draft.passwordMode === 'generate',
                  password: draft.passwordMode === 'manual' ? draft.password : undefined,
                  returnPassword: true,
                });
                close();
                await load();
                audit('Создал пользователя', res.user.username);
                toast('Пользователь создан.', 'ok');
                if (res.password) {
                  modal(
                    'Пароль пользователя',
                    <PasswordReveal password={res.password} />,
                    <button type="button" className="btn btn-primary" data-close>
                      Готово
                    </button>,
                  );
                }
              } catch (error) {
                toast(errText(error), 'err');
              }
            })();
          }}
        >
          Создать
        </button>
      </>,
    );
  };

  const openEdit = (user: PanelUserDto) => {
    let draft: Draft = {
      displayName: user.displayName,
      username: user.username,
      role: user.role,
      passwordMode: 'manual',
      password: '',
    };
    const close = modal(
      `Изменить · ${user.username}`,
      <UserForm
        mode="edit"
        initial={draft}
        roles={assignableRoles}
        onChange={(next) => {
          draft = next;
        }}
      />,
      <>
        <button type="button" className="btn" data-close>
          Отмена
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            void (async () => {
              try {
                const res = await Api.users.update(user.id, {
                  displayName: draft.displayName,
                  role: draft.role,
                });
                close();
                await load();
                audit('Изменил пользователя', res.user.username);
                toast('Изменения пользователя сохранены.', 'ok');
              } catch (error) {
                toast(errText(error), 'err');
              }
            })();
          }}
        >
          Сохранить
        </button>
      </>,
    );
  };

  const resetPassword = (user: PanelUserDto) => {
    let draft: Draft = {
      displayName: user.displayName,
      username: user.username,
      role: user.role,
      passwordMode: 'generate',
      password: '',
    };
    const close = modal(
      `Пароль · ${user.username}`,
      <UserForm
        mode="password"
        initial={draft}
        roles={assignableRoles}
        onChange={(next) => {
          draft = next;
        }}
      />,
      <>
        <button type="button" className="btn" data-close>
          Отмена
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            void (async () => {
              try {
                const res = await Api.users.update(user.id, {
                  generatePassword: draft.passwordMode === 'generate',
                  password: draft.passwordMode === 'manual' ? draft.password : undefined,
                  returnPassword: true,
                });
                close();
                await load();
                audit('Сбросил пароль', user.username);
                toast('Пароль пользователя изменён.', 'ok');
                if (res.password) {
                  modal(
                    'Новый пароль',
                    <PasswordReveal password={res.password} />,
                    <button type="button" className="btn btn-primary" data-close>
                      Готово
                    </button>,
                  );
                }
              } catch (error) {
                toast(errText(error), 'err');
              }
            })();
          }}
        >
          Сменить пароль
        </button>
      </>,
    );
  };

  const toggleDisabled = (user: PanelUserDto) => {
    const next = !user.disabled;
    confirmDialog(
      next ? 'Отключить пользователя?' : 'Включить пользователя?',
      next
        ? `«${user.username}» не сможет войти в панель, а его текущие сеансы завершатся. Включить его снова можно в любой момент.`
        : `«${user.username}» снова сможет входить в панель со своим паролем.`,
      () => {
        void (async () => {
          try {
            await Api.users.update(user.id, { disabled: next });
            await load();
            audit(next ? 'Отключил пользователя' : 'Включил пользователя', user.username);
            toast(next ? 'Пользователь отключён.' : 'Пользователь включён.', 'ok');
          } catch (error) {
            toast(errText(error), 'err');
          }
        })();
      },
      { yes: next ? 'Отключить' : 'Включить' },
    );
  };

  const removeUser = (user: PanelUserDto) => {
    confirmDialog(
      'Удалить пользователя?',
      `Учётная запись «${user.username}» будет удалена навсегда, восстановить её нельзя. Чтобы лишь временно закрыть доступ, отключите пользователя.`,
      () => {
        void (async () => {
          try {
            await Api.users.remove(user.id);
            await load();
            audit('Удалил пользователя', user.username);
            toast('Пользователь удалён.', 'ok');
          } catch (error) {
            toast(errText(error), 'err');
          }
        })();
      },
      { word: user.username, yes: 'Удалить' },
    );
  };

  if (!hasPermission('users.view')) return null;

  const disabledCount = users.filter((u) => u.disabled).length;
  const shown = filtered.filter((u) =>
    roleFilter === 'all' ? true : roleFilter === 'disabled' ? u.disabled : roleFilter === 'active' ? !u.disabled : u.role === roleFilter,
  ).sort((a, b) => sort === 'name'
    ? (a.displayName || a.username).localeCompare(b.displayName || b.username, 'ru')
    : b.createdAt.localeCompare(a.createdAt));
  const selectedRole = roles.find((role) => role.id === roleFilter);

  return (
    <section id="page-users" data-page="users" className="sv">
      <div className="sv-head">
        <h1 className="h1-lane"><LaneIcon kind="users" />Пользователи</h1>
        <div className="actions">
          {isSuperadmin ? <button type="button" className="btn sv-add" onClick={() => openRole()}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Создать роль
          </button> : null}
          {hasPermission('users.create') && assignableRoles.length > 0 ? (
          <button
            type="button"
            className="btn btn-primary sv-add"
            id="userAddBtn"
            onClick={openCreate}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
            Добавить пользователя
          </button>
          ) : null}
        </div>
      </div>

      <div className="us-layout">
        <aside className="us-roles" aria-label="Роли">
          <div className="us-roles-head"><h2>Роли</h2></div>
          <button type="button" className={roleFilter === 'all' ? 'active' : undefined} onClick={() => setRoleFilter('all')}>Все пользователи <span>{users.length}</span></button>
          {roles.map((role) => <div className="us-role-row" key={role.id}>
            <button type="button" className={roleFilter === role.id ? 'active' : undefined} onClick={() => setRoleFilter(role.id)}>{role.name}<span>{users.filter((user) => user.role === role.id).length}</span></button>
          </div>)}
          <h3 className="us-status-title">Статус</h3>
          <button type="button" className={roleFilter === 'active' ? 'active' : undefined} onClick={() => setRoleFilter('active')}>Активные <span>{users.length - disabledCount}</span></button>
          <button type="button" className={roleFilter === 'disabled' ? 'active' : undefined} onClick={() => setRoleFilter('disabled')}>Отключённые <span>{disabledCount}</span></button>
          {isSuperadmin && selectedRole ? <div className="us-role-actions">
            <button type="button" onClick={() => openRole(selectedRole)}>Права роли</button>
            {!selectedRole.builtin ? <button type="button" onClick={() => removeRole(selectedRole)}>Удалить роль</button> : null}
          </div> : null}
        </aside>
        <div className="us-main">
          <div className="sv-toolbar">
            <input id="userFilter" type="text" placeholder="Поиск по имени или логину" spellCheck={false} value={filter} onChange={(ev) => setFilter(ev.target.value)} />
            {filter || roleFilter !== 'all' ? <span className="us-result-count">{shown.length} из {users.length}</span> : null}
          </div>
          <div className="us-sort" role="group" aria-label="Сортировка пользователей">
            <button type="button" className={sort === 'newest' ? 'active' : undefined} onClick={() => setSort('newest')}>Новые сначала</button>
            <button type="button" className={sort === 'name' ? 'active' : undefined} onClick={() => setSort('name')}>По имени</button>
          </div>
      {loading ? (
        <div className="empty">Загружаем…</div>
      ) : shown.length === 0 ? (
        <div className="empty">Пользователей не найдено</div>
      ) : (
        <div className="us-grid" id="userBody">
          {shown.map((u) => {
            const self = u.id === userId;
            const manageable = assignableRoles.some((role) => role.id === u.role);
            return (
              <article key={u.id} data-id={u.id} className={`us-card${u.disabled ? ' off' : ''}`}>
                <header className="us-head">
                  <span
                    className="us-avatar"
                    style={{ background: avatarColor(u.username) }}
                    aria-hidden="true"
                  >
                    {initials(u.displayName || u.username)}
                  </span>
                  <div className="us-name">
                    <b>
                      {u.displayName || u.username}
                      {self ? <span className="us-self">Это вы</span> : null}
                    </b>
                    <small>@{u.username}</small>
                  </div>
                </header>
                <div className="us-meta">
                  <span className={`us-role ${u.role}`}>{roleLabel(u.role)}</span>
                  {u.disabled ? <span className="us-state">Отключён</span> : null}
                  <span className="us-date" title={fmtDate(u.createdAt)}>
                    добавлен{' '}
                    {new Date(u.createdAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                </div>
                <footer className="us-actions">
                  {hasPermission('users.edit') && manageable ? <button type="button" className="sv-act" onClick={() => openEdit(u)}>
                    {ICON.edit}
                    Изменить
                  </button> : null}
                  {hasPermission('users.edit') && manageable ? <button type="button" className="sv-act" onClick={() => resetPassword(u)}>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <rect x="5" y="11" width="14" height="9" rx="2" />
                      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
                    </svg>
                    Сменить пароль
                  </button> : null}
                  {hasPermission('users.edit') && manageable ? <button
                    type="button"
                    className="sv-act"
                    disabled={self}
                    onClick={() => toggleDisabled(u)}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <path d="M12 3v8" />
                      <path d="M6.3 6.8a8 8 0 1 0 11.4 0" />
                    </svg>
                    {u.disabled ? 'Включить' : 'Отключить'}
                  </button> : null}
                  {hasPermission('users.delete') && manageable ? <button
                    type="button"
                    className="sv-act danger"
                    disabled={self}
                    onClick={() => removeUser(u)}
                  >
                    {ICON.del}
                    Удалить
                  </button> : null}
                </footer>
              </article>
            );
          })}
        </div>
      )}
        </div>
      </div>
    </section>
  );
}

function UserForm({
  mode,
  initial,
  roles,
  onChange,
}: {
  mode: 'create' | 'edit' | 'password';
  initial: Draft;
  roles: PanelRoleDto[];
  onChange: (draft: Draft) => void;
}) {
  const [draft, setDraft] = useState(initial);
  const patch = (partial: Partial<Draft>) => {
    const next = { ...draft, ...partial };
    setDraft(next);
    onChange(next);
  };

  return (
    <div className="stack" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {mode !== 'password' ? (
        <>
          <div className="field-row">
            <label htmlFor="uDisplay">Отображаемое имя</label>
            <input
              id="uDisplay"
              type="text"
              value={draft.displayName}
              onChange={(ev) => patch({ displayName: ev.target.value })}
              placeholder="Например: Иван Петров"
              autoComplete="off"
            />
          </div>
          {mode === 'create' ? (
            <div className="field-row">
              <label htmlFor="uLogin">Логин</label>
              <input
                id="uLogin"
                type="text"
                value={draft.username}
                onChange={(ev) => patch({ username: ev.target.value })}
                placeholder="Например: ivan"
                spellCheck={false}
                autoComplete="off"
              />
            </div>
          ) : (
            <div className="field-row">
              <label>Логин</label>
              <input type="text" value={draft.username} disabled />
            </div>
          )}
          <div className="field-row">
            <label htmlFor="uRole">Роль</label>
            <select
              id="uRole"
              value={draft.role}
              onChange={(ev) => patch({ role: ev.target.value })}
            >
              {roles.map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}
            </select>
          </div>
        </>
      ) : null}

      {mode !== 'edit' ? (
        <>
          <div className="set-row">
            <div className="txt">
              <b>Сгенерировать пароль</b>
              <small>
                Панель сама придумает надёжный пароль из 16 символов. Выключите, чтобы задать пароль
                вручную.
              </small>
            </div>
            <label className="switch">
              <input
                type="checkbox"
                checked={draft.passwordMode === 'generate'}
                onChange={(ev) =>
                  patch({ passwordMode: ev.target.checked ? 'generate' : 'manual' })
                }
              />
              <i />
            </label>
          </div>
          {draft.passwordMode === 'manual' ? (
            <div className="field-row">
              <label htmlFor="uPass">Пароль</label>
              <input
                id="uPass"
                type="text"
                value={draft.password}
                onChange={(ev) => patch({ password: ev.target.value })}
                placeholder="Не меньше 8 символов"
                autoComplete="new-password"
                spellCheck={false}
              />
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

type RoleDraft = { name: string; permissions: string[] };

function RoleForm({ initial, readOnly, onChange }: { initial: RoleDraft; readOnly: boolean; onChange: (draft: RoleDraft) => void }) {
  const [draft, setDraft] = useState(initial);
  const setName = (name: string) => {
    const next = { ...draft, name };
    setDraft(next);
    onChange(next);
  };
  const toggle = (permission: string) => {
    const selected = new Set(draft.permissions);
    if (selected.has(permission)) selected.delete(permission);
    else {
      selected.add(permission);
      if (!permission.endsWith('.view')) selected.add(`${permission.split('.')[0]}.view`);
    }
    if (permission.endsWith('.view') && !selected.has(permission)) {
      const section = permission.split('.')[0];
      for (const action of ROLE_ACTIONS) selected.delete(`${section}.${action[0]}`);
    }
    const next = { ...draft, permissions: [...selected] };
    setDraft(next);
    onChange(next);
  };
  return <div className="us-role-form">
    <label htmlFor="roleName">Название роли</label>
    <input id="roleName" value={draft.name} disabled={readOnly} onChange={(event) => setName(event.target.value)} placeholder="Например: Аналитик" />
    <div className="us-permissions">
      <div className="us-permission-head"><span>Раздел</span>{ROLE_ACTIONS.map(([id, label]) => <span key={id}>{label}</span>)}</div>
      {ROLE_SECTIONS.map(([section, label]) => <div className="us-permission-row" key={section}>
        <strong>{label}</strong>
        {ROLE_ACTIONS.map(([action, actionLabel]) => {
          const permission = `${section}.${action}`;
          return <label key={action} aria-label={`${label}: ${actionLabel}`}>
            <input type="checkbox" checked={draft.permissions.includes(permission)} disabled={readOnly} onChange={() => toggle(permission)} />
          </label>;
        })}
      </div>)}
    </div>
  </div>;
}
